import { AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { ContentPublishingConnector, ContentPublishingDispatch } from "@/lib/composerCore";
import { formatJson, friendlyHttpStatusLabel } from "./dispatchDrawerData";
import { safeHref } from "@/lib/safeHref";

type Props = {
  dispatch: ContentPublishingDispatch;
  connector: ContentPublishingConnector | null;
  request: Record<string, unknown>;
  response: Record<string, unknown>;
  onCloseAndOpenConnector: (connectorId: string) => void;
};

const KitFailureCard = ({
  dispatch,
  connector,
  response,
  onCloseAndOpenConnector,
}: Pick<Props, "dispatch" | "connector" | "response" | "onCloseAndOpenConnector">) => {
  const statusFromResp = typeof response.status_code === "number" ? response.status_code : null;
  const friendly = statusFromResp !== null ? friendlyHttpStatusLabel(statusFromResp) : null;
  const rawMessage =
    (typeof response.error === "string" && response.error) ||
    dispatch.error_message ||
    "Kit draft creation failed.";
  const showSeparateFriendly = friendly !== null && !rawMessage.includes(friendly);
  const isAuthFailure = statusFromResp === 401 || statusFromResp === 403;

  return (
    <section
      className="space-y-2 rounded-md border border-destructive/40 bg-destructive/10 p-3"
      role="alert"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 text-destructive" aria-hidden="true" />
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            Kit draft creation failed
            {showSeparateFriendly ? (
              <span className="ml-2 align-middle">
                <Badge variant="destructive" className="text-[10px]">{friendly}</Badge>
              </span>
            ) : null}
          </p>
          <p className="break-words text-xs text-muted-foreground">{rawMessage}</p>
          {isAuthFailure ? (
            <p className="text-xs text-muted-foreground">
              Open this connector and use the{" "}
              <span className="font-medium text-foreground">Test</span> button to re-verify your Kit account, then update the API secret if needed.
            </p>
          ) : statusFromResp === 429 ? (
            <p className="text-xs text-muted-foreground">
              Kit is rate-limiting requests right now. Wait a minute, then dispatch again.
            </p>
          ) : null}
          {connector ? (
            <button
              type="button"
              onClick={() => onCloseAndOpenConnector(connector.id)}
              className="inline-flex items-center text-xs font-medium text-primary underline-offset-4 hover:underline"
            >
              Go to Kit connector →
            </button>
          ) : null}
        </div>
      </div>
    </section>
  );
};

const KitSuccessCard = ({ response }: { response: Record<string, unknown> }) => {
  const editUrl = safeHref(typeof response.edit_url === "string" ? response.edit_url : null);
  const broadcastId = typeof response.broadcast_id === "number" ? response.broadcast_id : null;
  return (
    <section className="space-y-2 rounded-md border border-primary/30 bg-primary/5 p-3">
      <p className="text-sm font-medium text-foreground">
        Draft created — review and send in Kit
      </p>
      <p className="text-xs text-muted-foreground">
        This dispatch created a draft broadcast in your Kit account. Nothing was sent automatically.
      </p>
      {editUrl ? (
        <a
          href={editUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center text-xs font-medium text-primary underline-offset-4 hover:underline"
        >
          Open draft in Kit{broadcastId ? ` (#${broadcastId})` : ""} →
        </a>
      ) : null}
    </section>
  );
};

export const WebhookDispatchSection = ({
  dispatch,
  connector,
  request,
  response,
  onCloseAndOpenConnector,
}: Props) => {
  const bodyPreview = typeof response.body_preview === "string" ? response.body_preview : null;

  return (
    <>
      {connector?.connector_type === "kit" && dispatch.status === "failed" ? (
        <KitFailureCard
          dispatch={dispatch}
          connector={connector}
          response={response}
          onCloseAndOpenConnector={onCloseAndOpenConnector}
        />
      ) : null}
      {connector?.connector_type === "kit" && dispatch.status === "succeeded" ? (
        <KitSuccessCard response={response} />
      ) : null}
      <section className="space-y-1">
        <p className="text-xs uppercase tracking-[0.08em] text-muted-foreground">Request envelope</p>
        <pre className="max-h-64 overflow-auto rounded-md border border-border bg-background p-3 text-[11px] leading-snug">
          {formatJson(request)}
        </pre>
      </section>
      <section className="space-y-1">
        <p className="text-xs uppercase tracking-[0.08em] text-muted-foreground">Response</p>
        <pre className="max-h-72 overflow-auto rounded-md border border-border bg-background p-3 text-[11px] leading-snug">
          {formatJson(response)}
        </pre>
        {bodyPreview ? (
          <p className="text-xs text-muted-foreground">
            Body preview is truncated to ~4 KiB.
          </p>
        ) : null}
      </section>
    </>
  );
};
